import type { DocStatus } from "./types";

export interface FrontMatter {
  notion_page_id?: string;
  notion_last_edited_time?: string;
  content_hash?: string;
  doc_type?: string;
  family?: string;
  version?: string;
  status?: DocStatus;
  title?: string;
  // One-line deck copied onto the public methodology or factsheet. When
  // absent, publish keeps the description already stored in Sanity.
  description?: string;
  ticker?: string;
  // Canonical public website slug (path under /static/). Overrides the slug
  // otherwise derived from the family.
  slug?: string;
  effective_date?: string;
  next_review?: string;
  supersedes?: string;
  // Index-methodology change classification. A material change requires a
  // minimum advance-notice window between announcement (notice_date) and the
  // effective_date; non-material changes are unconstrained.
  material_change?: boolean;
  notice_date?: string;
  template?: string;
  // Templated index families (M3): the canonical template path this doc was
  // instantiated from, and the single-line JSON bindings used to render it.
  template_source?: string;
  template_bindings?: string;
  [key: string]: unknown;
}

const FM_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

function parseYamlValue(raw: string): string {
  const val = raw.trim();
  if (
    (val.startsWith('"') && val.endsWith('"')) ||
    (val.startsWith("'") && val.endsWith("'"))
  ) {
    return val.slice(1, -1).replace(/\\"/g, '"');
  }
  return val;
}

function escapeYamlValue(val: string): string {
  // Front-matter values are single-line scalars; a literal newline here would
  // let a value inject additional YAML keys into this line-based format.
  const safe = val.replace(/[\r\n]+/g, " ");
  if (safe === "" || /[:#]/.test(safe) || safe.startsWith(" ")) {
    return `"${safe.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  }
  return safe;
}

export function parseFrontMatter(raw: string): {
  data: FrontMatter;
  content: string;
} {
  const match = raw.match(FM_RE);
  if (!match) return { data: {}, content: raw };

  const data: FrontMatter = {};
  for (const line of match[1].split("\n")) {
    const kv = line.match(/^([\w_]+):\s*(.*)$/);
    if (kv) data[kv[1]] = parseYamlValue(kv[2]);
  }
  return { data, content: raw.slice(match[0].length) };
}

export function stringifyFrontMatter(
  data: FrontMatter,
  content: string,
): string {
  const sorted = Object.fromEntries(
    Object.entries(data).sort(([a], [b]) => a.localeCompare(b)),
  );
  const lines = Object.entries(sorted).map(
    ([key, value]) => `${key}: ${escapeYamlValue(String(value ?? ""))}`,
  );
  const body = content.replace(/^\n+/, "");
  return `---\n${lines.join("\n")}\n---\n\n${body}`;
}
